/* eslint-disable no-console */
/* eslint-disable no-use-before-define */
import fixWebmDuration from 'fix-webm-duration';

// Keep references to active streams so we can stop tracks reliably
let activeStreams = [];

function selectVideoMime({ isFirefox, isSafari }) {
  if (isSafari) return 'video/mp4';
  // Firefox formatting quirk retained from legacy
  if (isFirefox) return 'video/webm;codecs:vp9';
  return 'video/webm;codecs=vp9';
}

function selectAudioMime({ isSafari }) {
  return isSafari ? 'audio/mp4' : 'audio/webm';
}

export function stopAllTracks() {
  activeStreams.forEach((stream) => {
    try {
      stream.getTracks().forEach((t) => t.stop());
    } catch (e) {
      // noop
    }
  });
  activeStreams = [];
}

export function createRecordingController({ mediaRecorderRef, streamRef, videoRef, isFirefox, isSafari }) {
  // shared helper to finalize blob with duration fix when needed
  async function finalizeBlob({ chunks, durationMs, type, isSafariLike }) {
    const rawBlob = new Blob(chunks, { type });
    if (isSafariLike) {
      return rawBlob;
    }
    try {
      const fixed = await fixWebmDuration(rawBlob, durationMs, { logger: false });
      return fixed;
    } catch (e) {
      // Fallback to raw blob if fixing fails
      return rawBlob;
    }
  }

  async function startCamera({ onReady, onStart, onStop, onData, onError, facingMode = 'user' } = {}) {
    const chunks = [];
    let startTime = 0;
    const options = { mimeType: selectVideoMime({ isFirefox, isSafari }) };

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: {
          facingMode: { ideal: facingMode },
          width: { ideal: 1920, max: 1920 },
          height: { ideal: 1080, max: 1080 },
        },
      });

      // eslint-disable-next-line no-param-reassign
      streamRef.current = stream;
      activeStreams.push(stream);

      if (videoRef?.current) {
        // eslint-disable-next-line no-param-reassign
        videoRef.current.srcObject = stream;
        try {
          // Ensure inline autoplay works
          // eslint-disable-next-line no-param-reassign
          videoRef.current.muted = true;
          // eslint-disable-next-line no-param-reassign
          videoRef.current.playsInline = true;
          // Attempt immediate play as some browsers won't fire onloadedmetadata reliably
          videoRef.current.play().catch(() => {});
        } catch (e) { /* noop */ }
        // eslint-disable-next-line no-param-reassign
        videoRef.current.onloadedmetadata = () => {
          try {
            videoRef.current.play();
          } catch (e) { /* noop */ }
          if (onReady) onReady();
        };
      }
      // No video element bound; still notify ready so UI can proceed
      if (!videoRef?.current && onReady) onReady();

      // eslint-disable-next-line no-param-reassign
      mediaRecorderRef.current = new MediaRecorder(stream, options);
      mediaRecorderRef.current.addEventListener('dataavailable', (e) => {
        if (e.data && e.data.size > 0) {
          chunks.push(e.data);
          if (onData) onData(e.data);
        }
      });
      mediaRecorderRef.current.addEventListener('start', () => {
        startTime = Date.now();
        if (onStart) onStart();
      });
      mediaRecorderRef.current.addEventListener('stop', async () => {
        try {
          stream.getTracks().forEach((t) => t.stop());
        } catch (e) { /* noop */ }
        const duration = Date.now() - startTime;
        const blob = await finalizeBlob({
          chunks,
          durationMs: duration,
          type: isSafari ? 'video/mp4' : 'video/webm',
          isSafariLike: !!isSafari,
        });
        if (onStop) onStop({ blob, durationMs: duration });
      });

      return { stream };
    } catch (error) {
      if (onError) onError(error);
      throw error;
    }
  }

  async function startScreen({ onReady, onStart, onStop, onData, onError } = {}) {
    const chunks = [];
    let startTime = 0;
    const options = { mimeType: selectVideoMime({ isFirefox, isSafari }) };

    try {
      const screen = await navigator.mediaDevices.getDisplayMedia({
        video: { mediaSource: 'screen', width: { ideal: 1920, max: 1920 }, height: { ideal: 1080, max: 1080 } },
      });
      const mic = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      const combined = new MediaStream([...screen.getTracks(), ...mic.getTracks()]);

      // Stop recording if the user ends sharing from the browser UI
      const screenTrack = screen.getVideoTracks?.()[0];
      if (screenTrack) {
        screenTrack.addEventListener('ended', () => {
          try {
            if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
              mediaRecorderRef.current.stop();
            }
          } catch (e) { /* noop */ }
        });
      }

      // eslint-disable-next-line no-param-reassign
      streamRef.current = combined;
      activeStreams.push(screen);

      // eslint-disable-next-line no-param-reassign
      mediaRecorderRef.current = new MediaRecorder(combined, options);
      mediaRecorderRef.current.addEventListener('dataavailable', (e) => {
        if (e.data && e.data.size > 0) {
          chunks.push(e.data);
          if (onData) onData(e.data);
        }
      });
      mediaRecorderRef.current.addEventListener('start', () => {
        startTime = Date.now();
        if (onStart) onStart();
      });
      mediaRecorderRef.current.addEventListener('stop', async () => {
        try {
          combined.getTracks().forEach((t) => t.stop());
        } catch (e) { /* noop */ }
        const duration = Date.now() - startTime;
        const blob = await finalizeBlob({
          chunks,
          durationMs: duration,
          type: isSafari ? 'video/mp4' : 'video/webm',
          isSafariLike: !!isSafari,
        });
        if (onStop) onStop({ blob, durationMs: duration });
      });

      if (videoRef?.current) {
        // eslint-disable-next-line no-param-reassign
        videoRef.current.srcObject = combined;
        try {
          // eslint-disable-next-line no-param-reassign
          videoRef.current.muted = true;
          // eslint-disable-next-line no-param-reassign
          videoRef.current.playsInline = true;
          videoRef.current.play().catch(() => {});
        } catch (e) { /* noop */ }
        // eslint-disable-next-line no-param-reassign
        videoRef.current.onloadedmetadata = () => {
          try {
            videoRef.current.play();
          } catch (e) { /* noop */ }
          if (onReady) onReady();
        };
      }
      if (!videoRef?.current && onReady) onReady();

      return { stream: combined };
    } catch (error) {
      if (onError) onError(error);
      throw error;
    }
  }

  async function startAudio({ onReady, onStart, onStop, onData, onError } = {}) {
    const chunks = [];
    let startTime = 0;
    const options = { mimeType: selectAudioMime({ isSafari }) };

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // eslint-disable-next-line no-param-reassign
      streamRef.current = stream;
      activeStreams.push(stream);

      // eslint-disable-next-line no-param-reassign
      mediaRecorderRef.current = new MediaRecorder(stream, options);
      mediaRecorderRef.current.addEventListener('dataavailable', (e) => {
        if (e.data && e.data.size > 0) {
          chunks.push(e.data);
          if (onData) onData(e.data);
        }
      });
      mediaRecorderRef.current.addEventListener('start', () => {
        startTime = Date.now();
        if (onStart) onStart();
      });
      mediaRecorderRef.current.addEventListener('stop', async () => {
        try {
          stream.getTracks().forEach((t) => t.stop());
        } catch (e) { /* noop */ }
        const duration = Date.now() - startTime;
        const blob = await finalizeBlob({
          chunks,
          durationMs: duration,
          type: isSafari ? 'audio/mp4' : 'audio/webm',
          isSafariLike: !!isSafari,
        });
        if (onStop) onStop({ blob, durationMs: duration });
      });

      if (onReady) onReady();
      return { stream };
    } catch (error) {
      if (onError) onError(error);
      throw error;
    }
  }

  function pause() {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.pause();
      }
    } catch (e) { /* noop */ }
  }

  function resume() {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
        mediaRecorderRef.current.resume();
      }
    } catch (e) { /* noop */ }
  }

  return { startCamera, startScreen, startAudio, pause, resume };
}

